<template>
  <UIDialogTransition>
    <UIDialogRoot modal :visible="isVisible">
      <UIDialog center size="lg">
        <UIDialogContent ref="body">
          <UIDialogHeader class="border-0">
            <LogoIcon class="mx-auto" style="width: 126px" />
          </UIDialogHeader>

          <UIDialogBody class="text-center py-0">
            <h6 class="fw-bold mb-1">{{ noticeData?.title_text }}</h6>
            <div class="sm" v-html="noticeData?.cookie_text"></div>
          </UIDialogBody>

          <UIDialogFooter class="justify-content-center border-0">
            <UIButton variant="outline-primary" @click="declineCookies()">
              {{ noticeData?.decline_button_text }}
            </UIButton>

            <UIButton variant="primary" @click="consentCookies()">
              {{ noticeData?.accept_button_text }}
            </UIButton>
          </UIDialogFooter>
        </UIDialogContent>
      </UIDialog>
    </UIDialogRoot>
  </UIDialogTransition>
</template>

<script lang="ts">
export type CookieNoticeId = number;
export type CountryCode = string;

export type CookieNoticeType = {
  id: CookieNoticeId;
  region: CountryCode;
  title_text: string;
  cookie_text: string;
  accept_button_text: string;
  decline_button_text: string;
};

enum CookiePolicy {
  Accepted = "accepted",
  Declined = "declined",
}
</script>

<script setup lang="ts">
import LogoIcon from "@/assets/icons/2simple.svg?component";
import { defaultCookieOptions } from "@/common/default-cookie-options";
import { gatekeeperCookieId } from "@/components/login-gatekeeper/LoginGatekeeper.vue";
import {
  UIDialog,
  UIDialogBody,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
  UIDialogRoot,
} from "@/components/UI/dialog";
import UIDialogTransition from "@/components/UI/dialog/UIDialogTransition.vue";
import UIButton from "@/components/UI/UIButton.vue";
import { useCookies } from "@vueuse/integrations/useCookies";
import { computed, onBeforeMount, onMounted, ref } from "vue";

const body = ref<InstanceType<typeof UIDialogContent> | null>(null);
const ipRegion = window.IPRegion || "uk";
const noticeData = window.CookieNotices[ipRegion] ?? null;

const cookieId = `cookies_${ipRegion}`;
const cookies = useCookies([cookieId, gatekeeperCookieId]);

// Visible after the GateKeeper component.
const isVisible = computed<boolean>(
  () => !cookies.get(cookieId) && cookies.get(gatekeeperCookieId)
);

const consentCookies = () => {
  cookies.set(cookieId, CookiePolicy.Accepted, defaultCookieOptions);
};

const declineCookies = () => {
  cookies.set(cookieId, CookiePolicy.Declined, defaultCookieOptions);
};

// Auto-consent based on IP locale.
onBeforeMount(() => {
  if (!noticeData) {
    consentCookies();
  }
});

onMounted(() => {
  changeDocumentLinks();
});

// this is replacing wagtail-formatted links with HTML links.
// It was done here probably because this component rendered after the rest of the page.
// TODO: move this logic into the component the renders wagtail HTML i.e. <RichText />.
function changeDocumentLinks() {
  if (!body.value) {
    return;
  }
  const links = body.value?.$el.querySelectorAll("a[linktype='document']");

  links.forEach((link: HTMLLinkElement) => {
    link.href = "/docs/" + link.id;
    link.target = "_blank";
  });
}
</script>
